import { AccountBalanceOutlined, CalendarMonthOutlined } from '@mui/icons-material';
import { Box, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import React from 'react';

import DrawerLink from '../../components/common/drawers/ModuleDrawer/DrawerLink';
import { AttendanceStatus, Event, EventStatus, eventBaseColor } from '../../types/Event';
import { UserConfigurableModule } from '../../types/User';
import { humanizeEventDateTimes } from '../../utility/dateUtils';
import { attendanceStatusColor } from '../../components/dashboard/EventsPanel/EventTile';
import {
  scheduleRowTileContainerSx,
  scheduleRowTileContentSx,
  scheduleRowTileHeaderLabelSx,
  scheduleRowTileHeaderSx,
  scheduleRowTileIndicatorSx,
  scheduleRowTileSecondaryIconSx,
  scheduleRowTileSecondaryLabelSx,
  scheduleRowTileSecondaryTextSx,
  scheduleRowTileTextContainerSx,
} from './employeesScheduleStyles';

interface IScheduleRowTileProps {
  event: Event;
  attendance: AttendanceStatus;
}

interface StatusIndicatorConfig {
  title: EventStatus | AttendanceStatus;
  color: string;
  striped?: boolean;
}

// Returns the configuration for the status indicator based on the event status and attendance status.
const getStatusIndicatorConfig = (eventStatus: EventStatus, attendance: AttendanceStatus, theme: Theme): StatusIndicatorConfig => {
  switch (eventStatus) {
    case EventStatus.PLANNING:
    case EventStatus.SCHEDULED:
      return {
        title: eventStatus,
        color: eventBaseColor(EventStatus.PLANNING),
        striped: true,
      };

    case EventStatus.PUBLISHED:
    case EventStatus.IN_PROGRESS:
      return {
        title: attendance,
        color: attendanceStatusColor(attendance, theme),
      };

    case EventStatus.COMPLETE:
    case EventStatus.CANCELLED:
      return {
        title: eventStatus,
        color: theme.palette.common.black,
      };

    default:
      return {
        title: eventStatus,
        color: eventBaseColor(eventStatus),
      };
  }
};

export default function ScheduleRowTile({ event, attendance }: IScheduleRowTileProps) {
  const theme = useTheme();

  const indicator = getStatusIndicatorConfig(event.status, attendance, theme);

  const dateLabel = humanizeEventDateTimes(event);

  return (
    <Box sx={scheduleRowTileContainerSx}>
      <Box
        title={indicator.title}
        aria-label={indicator.title}
        sx={scheduleRowTileIndicatorSx({
          color: indicator.color,
          stripeColor: theme.palette.divider,
          striped: indicator.striped,
        })}
      />

      <Box sx={scheduleRowTileContentSx}>
        <Box sx={scheduleRowTileHeaderSx}>
          <DrawerLink module={UserConfigurableModule.EVENTS} uuid={event.uuid}>
            <Typography title={event.name} sx={scheduleRowTileHeaderLabelSx}>
              {event.name}
            </Typography>
          </DrawerLink>
        </Box>

        <Box sx={scheduleRowTileTextContainerSx}>
          <Box sx={scheduleRowTileSecondaryTextSx}>
            <CalendarMonthOutlined sx={scheduleRowTileSecondaryIconSx} />

            <Typography title={dateLabel} sx={scheduleRowTileSecondaryLabelSx}>
              {dateLabel}
            </Typography>
          </Box>

          <Box sx={scheduleRowTileSecondaryTextSx}>
            <AccountBalanceOutlined sx={scheduleRowTileSecondaryIconSx} />

            <DrawerLink module={UserConfigurableModule.ACCOUNTS} uuid={event.account_uuid}>
              <Typography title={event.account_display_name} sx={scheduleRowTileSecondaryLabelSx}>
                {event.account_display_name}
              </Typography>
            </DrawerLink>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
